import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import {
  Breadcrumb,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Button,
  Spinner,
  Badge,
} from "react-bootstrap";
import {
  fetchVendorProfileById,
  approveVendorProfile,
  rejectVendorProfile,
  clearError,
  clearSuccess,
} from "../../redux/slices/vendorProfileSlice";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const VendorDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    currentVendorProfile: vendor,
    isLoading,
    error,
    success,
    isUpdating,
  } = useSelector((state) => state.vendorProfile);

  useEffect(() => {
    if (id) {
      dispatch(fetchVendorProfileById(id));
    }
  }, [dispatch, id]);

  // Clear messages and show toasts
  useEffect(() => {
    if (error) {
      toast.error(error);
      const timer = setTimeout(() => dispatch(clearError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  useEffect(() => {
    if (success) {
      toast.success(success);
      const timer = setTimeout(() => dispatch(clearSuccess()), 5000);
      return () => clearTimeout(timer);
    }
  }, [success, dispatch]);

  const handleApprove = () => {
    dispatch(approveVendorProfile(id));
  };

  const handleReject = () => {
    dispatch(rejectVendorProfile(id));
  };

  const handleEdit = () => {
    navigate(`/admin/vendors/edit/${id}`);
  };

  const handleBack = () => {
    navigate("/admin/vendors");
  };

  if (isLoading) {
    return (
      <Container className="view-form">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
        </div>
      </Container>
    );
  }

  if (!vendor) {
    return (
      <Container className="view-form">
        <div className="text-center mt-4">
          <h4>Vendor not found</h4>
          <Button variant="primary" onClick={handleBack}>
            Back to Vendors
          </Button>
        </div>
      </Container>
    );
  }

  return (
    <Container className="view-form">
      <Row>
        {/* <Col lg={12}>
          <Breadcrumb>
            <Breadcrumb.Item onClick={() => navigate("/admin")}>
              Home
            </Breadcrumb.Item>
            <Breadcrumb.Item onClick={() => navigate("/admin/vendors")}>
              Vendors
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Vendor Details</Breadcrumb.Item>
          </Breadcrumb>
        </Col> */}

        <Col lg={12}>
          <div className="form-header d-flex justify-content-between align-items-center px-3 py-2 rounded-top">
            <h5 className="mb-0 fw-semibold text-light">Vendor Details</h5>
          </div>
          <Card className="shadow">
            <CardBody>
              <Row>
                <Col lg={12} className="d-flex align-items-center gap-3">
                  <Row>
                    <Col lg={2} className="bg-light rounded-4">
                      <Row>
                        <Col lg={12} className="my-2">
                          <div className="image-box">
                            <img
                              src={
                                vendor?.image_url && vendor.image_url.trim() !== ""
                                  ? vendor.image_url
                                  : "/not-img-found.png"
                              }
                              alt="vendor"
                              style={{
                                width: "170px",
                                height: "170px",
                                objectFit: "cover",
                                borderRadius: "10%",
                                boxShadow: "rgba(99, 99, 99, 0.2) 0px 2px 8px 0px",
                                cursor: "pointer",
                                display: "block",
                                margin: "0 auto",
                                marginTop: "5px",
                              }}
                            />
                          </div>
                        </Col>

                        <Col lg={12} className="my-2 mt-4 text-center">
                          <label className="fw-bold">
                            <h6>Business Name</h6>
                          </label>
                          <h5
                            className="mb-0"
                            style={{
                              textAlign: "center",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "20px",
                            }}
                          >
                            {vendor?.business_name || "-"}
                          </h5>
                        </Col>

                        <Col lg={12} className="my-2 text-center" >
                          <label className="fw-bold">
                            <h6>Status</h6>
                          </label>
                          <h5
                            className="mb-0"
                            style={{
                              textAlign: "center",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "20px",
                            }}
                          >
                            <Badge
                              bg={vendor?.status === "Active" ? "success" : "secondary"}
                            >
                              {vendor?.status || "In-Active"}
                            </Badge>
                          </h5>
                        </Col>
                      </Row>
                    </Col>

                    <Col lg={10}>
                      <Row className="my-2 view-form">
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Contact Person</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.contact_person || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Business Email</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.business_email || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Business Phone</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.business_phone || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">GST Number</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.gst_no || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Payment Terms</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.payment_terms || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Registration Date</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.created_at
                              ? new Date(vendor.created_at).toLocaleDateString("en-GB")
                              : "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Last Updated</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.updated_at
                              ? new Date(vendor.updated_at).toLocaleDateString("en-GB")
                              : "-"}
                          </p>
                        </Col>

                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Aadhar Number</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.aadhar_number || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Account Holder Name</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.account_holder_name || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Account Number</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.bank_account_number || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Bank Name</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.bank_name || "-"}
                          </p>
                        </Col>
                        <Col lg={4} className="my-2">
                          <label className="fw-bold">IFSC Code</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.ifsc_code || "-"}
                          </p>
                        </Col>

                        <Col lg={4} className="my-2">
                          <label className="fw-bold">Pan Number</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.pan_number || "-"}
                          </p>
                        </Col>

                        <Col lg={12} className="my-2 padd-left-0">
                          <label className="fw-bold">Business Address</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.business_address || "-"}
                          </p>
                        </Col>

                        <Col lg={12} className="my-2 padd-left-0">
                          <label className="fw-bold">Business Description</label>
                          <p
                            className="mb-0"
                            style={{
                              textAlign: "left",
                              borderBottom: "1px solid #ccc",
                              paddingBottom: "5px",
                            }}
                          >
                            {vendor?.business_description || "-"}
                          </p>
                        </Col>
                        {/* </Row> */}
                      </Row>
                    </Col>
                  </Row>

                </Col>
              </Row>



            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default VendorDetails;
